import React, { useState, useEffect } from "react"
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Space,
  Select,
  Card,
  Typography,
  Modal,
} from "antd"
import {
  UserOutlined,
  IdcardOutlined,
  PhoneOutlined,
  BankOutlined,
  NumberOutlined,
  SafetyCertificateOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  PlusOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons"
import { PersianDatePickerInline } from "components/Common/PersianDatePickerInline"
import MultiLocation from "components/Common/MultiLocation"

const { Title, Text } = Typography

const InstallerModal = ({
  isModalOpen,
  onClose,
  onSubmit,
  editingRecord,
  packageCompanyType,
}) => {
  const [installerForm] = Form.useForm()
  const [modalLoading, setModalLoading] = useState(false)
  const [solarMembershipDate, setSolarMembershipDate] = useState("")
  const [solarWorkPermitDateOfIssue, setSolarWorkPermitDateOfIssue] =
    useState("")
  const [solarLicenseExpire, setSolarLicenseExpire] = useState("")
  // Controlled sectionIds state — set directly so MultiLocation always gets the correct value
  const [sectionIds, setSectionIds] = useState([])

  const editingId = editingRecord?.id || null

  // Reset form when modal opens/closes or record changes
  useEffect(() => {
    if (isModalOpen) {
      if (editingRecord) {
        // Edit mode: derive sectionIds — prefer sections[] from backend (freshest),
        // then sectionIds, then legacy sectionId
        const derived =
          editingRecord.sections?.length > 0
            ? editingRecord.sections.map(s => s.id)
            : editingRecord.sectionIds?.length > 0
            ? editingRecord.sectionIds
            : editingRecord.sectionId
            ? [editingRecord.sectionId]
            : []

        setSectionIds(derived)
        installerForm.setFieldsValue({
          ...editingRecord,
          sectionIds: derived,
        })
      } else {
        // Create mode: reset form
        setSectionIds([])
        installerForm.resetFields()
        setSolarMembershipDate("")
        setSolarWorkPermitDateOfIssue("")
        setSolarLicenseExpire("")
      }
    }
  }, [isModalOpen, editingRecord])

  const handleCloseModal = () => {
    setSectionIds([])
    installerForm.resetFields()
    setSolarMembershipDate("")
    setSolarWorkPermitDateOfIssue("")
    setSolarLicenseExpire("")
    onClose()
  }

  const handleSubmit = values => {
    setModalLoading(true)
    const updatedData = {
      ...values,
      isActive: true,
      solarMembershipDate: solarMembershipDate?.persian || "",
      solarWorkPermitDateOfIssue: solarWorkPermitDateOfIssue?.persian || "",
      solarLicenseExpire: solarLicenseExpire?.persian || "",
      provinceName: "تهران",
      id: editingId,
      sectionIds: values.sectionIds || [],
    }

    onSubmit(updatedData)

    setTimeout(() => {
      setModalLoading(false)
      handleCloseModal()
    }, 500)
  }

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            borderBottom: "1px solid #f0f0f0",
            paddingBottom: "16px",
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: editingId
                ? "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)"
                : "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {editingId ? (
              <EditOutlined style={{ fontSize: "24px", color: "white" }} />
            ) : (
              <PlusOutlined style={{ fontSize: "24px", color: "white" }} />
            )}
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: "#1f2937" }}>
              {editingId ? "ویرایش نصاب" : "افزودن نصاب جدید"}
            </Title>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              {editingId
                ? "اطلاعات نصاب را ویرایش کنید"
                : "اطلاعات نصاب جدید را وارد کنید"}
            </Text>
          </div>
        </div>
      }
      open={isModalOpen}
      onCancel={handleCloseModal}
      width={800}
      centered
      destroyOnClose
      footer={null}
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: "calc(100vh - 200px)",
          overflowY: "auto",
          padding: "0 24px 24px",
        },
      }}
    >
      <Form
        form={installerForm}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark="optional"
      >
        {/* Personal Information Section */}
        <Card
          type="inner"
          title={
            <Space>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background:
                    "linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <UserOutlined style={{ color: "#1890ff", fontSize: "16px" }} />
              </div>
              <Text strong style={{ fontSize: "15px" }}>
                اطلاعات شخصی
              </Text>
            </Space>
          }
          style={{
            marginBottom: "16px",
            borderRadius: "12px",
            border: "1px solid #e8e8e8",
          }}
          headStyle={{ background: "#fafafa", borderRadius: "12px 12px 0 0" }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="نام کامل"
                name="fullName"
                rules={[{ required: true, message: "نام را وارد کنید" }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="نام و نام خانوادگی"
                  size="large"
                  style={{ borderRadius: "8px" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="کد ملی"
                name="naCode"
                rules={[{ required: true, message: "کد ملی را وارد کنید" }]}
              >
                <Input
                  prefix={<IdcardOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="کد ملی 10 رقمی"
                  maxLength={10}
                  size="large"
                  style={{ borderRadius: "8px" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="شماره موبایل"
                name="mobileNumber"
                rules={[
                  { required: true, message: "شماره موبایل را وارد کنید" },
                ]}
              >
                <Input
                  prefix={<PhoneOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="09XXXXXXXXX"
                  maxLength={11}
                  size="large"
                  style={{ borderRadius: "8px" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Company Information Section */}
        <Card
          type="inner"
          title={
            <Space>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background:
                    "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BankOutlined style={{ color: "#52c41a", fontSize: "16px" }} />
              </div>
              <Text strong style={{ fontSize: "15px" }}>
                اطلاعات شرکت
              </Text>
            </Space>
          }
          style={{
            marginBottom: "16px",
            borderRadius: "12px",
            border: "1px solid #e8e8e8",
          }}
          headStyle={{ background: "#fafafa", borderRadius: "12px 12px 0 0" }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="نام شرکت"
                name="packageCompanyTypeEnum"
                rules={[
                  { required: true, message: "نام شرکت را انتخاب کنید" },
                ]}
              >
                <Select
                  placeholder="انتخاب شرکت"
                  options={packageCompanyType}
                  size="large"
                  suffixIcon={<BankOutlined />}
                  style={{ borderRadius: "8px" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="کد شرکت" name="companyCode">
                <Input
                  prefix={<NumberOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder="کد شرکت"
                  size="large"
                  style={{ borderRadius: "8px" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="شماره پروانه" name="licenseNumber">
                <Input
                  prefix={
                    <SafetyCertificateOutlined style={{ color: "#bfbfbf" }} />
                  }
                  placeholder="شماره پروانه"
                  size="large"
                  style={{ borderRadius: "8px" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Location Information Section */}
        <Card
          type="inner"
          title={
            <Space>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background:
                    "linear-gradient(135deg, #fffbe6 0%, #ffe58f 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <EnvironmentOutlined
                  style={{ color: "#faad14", fontSize: "16px" }}
                />
              </div>
              <Text strong style={{ fontSize: "15px" }}>
                اطلاعات مکانی
              </Text>
            </Space>
          }
          style={{
            marginBottom: "16px",
            borderRadius: "12px",
            border: "1px solid #e8e8e8",
          }}
          headStyle={{ background: "#fafafa", borderRadius: "12px 12px 0 0" }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24}>
              <Form.Item label="مناطق فعالیت" name="sectionIds">
                <MultiLocation
                  value={sectionIds}
                  onChange={ids => {
                    setSectionIds(ids)
                    installerForm.setFieldsValue({ sectionIds: ids })
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="آدرس" name="address">
                <Input.TextArea
                  rows={3}
                  placeholder="آدرس کامل..."
                  style={{ fontSize: "14px", borderRadius: "8px" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Date Information Section */}
        <Card
          type="inner"
          title={
            <Space>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background:
                    "linear-gradient(135deg, #fff0f6 0%, #ffadd2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CalendarOutlined
                  style={{ color: "#eb2f96", fontSize: "16px" }}
                />
              </div>
              <Text strong style={{ fontSize: "15px" }}>
                تاریخ‌های مهم
              </Text>
            </Space>
          }
          style={{
            marginBottom: "24px",
            borderRadius: "12px",
            border: "1px solid #e8e8e8",
          }}
          headStyle={{ background: "#fafafa", borderRadius: "12px 12px 0 0" }}
        >
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={8}>
              <Form.Item label="تاریخ عضویت" name="julianMembershipDate">
                <PersianDatePickerInline
                  defaultDate={installerForm
                    .getFieldValue("julianMembershipDate")
                    ?.toString()
                    ?.substring(0, 10)}
                  setPersianDate={data => {
                    setSolarMembershipDate(data)
                    installerForm.setFieldsValue({
                      julianMembershipDate: data?.gregorian,
                    })
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="تاریخ صدور پروانه"
                name="julianWorkPermitDateOfIssue"
              >
                <PersianDatePickerInline
                  defaultDate={installerForm
                    .getFieldValue("julianWorkPermitDateOfIssue")
                    ?.toString()
                    ?.substring(0, 10)}
                  setPersianDate={date => {
                    setSolarWorkPermitDateOfIssue(date)
                    installerForm.setFieldsValue({
                      julianWorkPermitDateOfIssue: date?.gregorian,
                    })
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="تاریخ انقضای پروانه"
                name="julianLicenseExpire"
              >
                <PersianDatePickerInline
                  defaultDate={installerForm
                    .getFieldValue("julianLicenseExpire")
                    ?.toString()
                    ?.substring(0, 10)}
                  setPersianDate={date => {
                    setSolarLicenseExpire(date)
                    installerForm.setFieldsValue({
                      julianLicenseExpire: date?.gregorian,
                    })
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            paddingTop: "16px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button
            size="large"
            icon={<CloseOutlined />}
            onClick={handleCloseModal}
            style={{ borderRadius: "8px", minWidth: "120px" }}
          >
            انصراف
          </Button>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={modalLoading}
            icon={editingId ? <SaveOutlined /> : <PlusOutlined />}
            style={{
              borderRadius: "8px",
              minWidth: "140px",
              background: editingId
                ? "linear-gradient(135deg, #1890ff 0%, #096dd9 100%)"
                : "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
              border: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            {editingId ? "ذخیره تغییرات" : "افزودن نصاب"}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

export default InstallerModal
